import { useCallback, useRef, useState } from "react"
import { GetApp } from "../../App"
import { GetShip } from "../../App/Context"
import { useEditState, type EditStateCallbackEvents } from "../../App/useEditState"
import { logger } from "../../App/Logger"
import { useShipUserWriters } from "../ShipData/UsersDataContext"
import { UseShipUserEditOptions } from "./Params"
import type { EditGroupData } from "../../Components/EditGroup"
import type { IShipUserRequest } from "../../Types"

// Shared hook — contains all logic consumed by both layout components

export function useShipUserEdit({ user }: UseShipUserEditOptions) {
  const app = GetApp();
  const t = app.getT("ShipSettingsView");
  const ship = GetShip();
  // Crew writes go through the enclosing UsersView's store-backed optimistic writer
  // boundary, falling back to typed-REST when this edit view renders outside one
  // (standalone catalog/tests). (issue #1186)
  const writers = useShipUserWriters(ship.id);
  const [edited, markEdited, resetEdited] = useEditState();
  const [draft, setDraft] = useState({ name: user.name ?? "", shortname: user.shortname ?? "" });
  const revision = useRef(0);
  const onEdited = useCallback((event?: EditStateCallbackEvents) => {
    const field = event?.target.name;
    const value = event?.target.value;
    setDraft((previous) => ({
      ...(edited ? previous : { name: user.name ?? "", shortname: user.shortname ?? "" }),
      ...(field === "name" || field === "shortname" ? { [field]: value ?? "" } : {}),
    }));
    revision.current += 1;
    markEdited();
  }, [edited, markEdited, user.name, user.shortname]);

  const updateShipUserActive = useCallback(async (active: boolean) => {
    await writers.updateShipUser(user.id, { active: active ? 1 : 0 });
  }, [writers, user]);

  const removeShipUser = useCallback(async () => {
    await writers.removeShipUser(user.id);
  }, [writers, user]);

  const updateShipUser = useCallback(async (data: FormData, values: EditGroupData) => {
    logger.debug("updateShipUser", user.id);
    const savingRevision = revision.current;
    // Omit absent levels and unchanged inherited names: neither is a ship override.
    const name = data.get("name");
    const shortname = data.get("shortname");
    const update: IShipUserRequest = {
      ...(typeof name === "string" && name !== (user.name ?? "") ? { name } : {}),
      ...(typeof shortname === "string" && shortname !== (user.shortname ?? "") ? { shortname } : {}),
      ...(typeof values.shipUserCrewRole === "number" && values.shipUserCrewRole !== user.shipUserCrewRole ? { shipUserCrewRole: values.shipUserCrewRole } : {}),
      ...(typeof values.shipUserLevel === "number" && values.shipUserLevel !== user.shipUserLevel ? { shipUserLevel: values.shipUserLevel } : {}),
    };
    try {
      await writers.updateShipUser(user.id, update);
    } catch (error) {
      resetEdited();
      throw error;
    }
    if (revision.current === savingRevision) resetEdited();
  }, [writers, user, resetEdited]);

  const { name, shortname } = edited ? draft : { name: user.name ?? "", shortname: user.shortname ?? "" };
  return { app, t, edited, name, shortname, onEdited, updateShipUser, updateShipUserActive, removeShipUser };
}
